import { create } from 'zustand'
import type { PriceAlertStatusFilter } from './price-alert-types'

/**
 * View state the Price Alerts panel shares with its title-bar controls.
 *
 * The status filter and the alert count live in the panel HEADER (rendered by
 * the workspace mosaic via `renderToolbarExtras` / `renderToolbarExtrasRight`),
 * while the list query lives in the panel BODY. The header writes the filter
 * and reads the count; the body reads the filter and publishes the count.
 * Same shape as `useGridRowCountStore`: transient, keyed by panelId, no
 * persistence.
 */
export interface PriceAlertCounts {
  /** Rows in the current list. */
  total: number
  /** Of those, still waiting to fire. */
  waiting: number
  /** The user's unexpired active alerts across every filter, and the cap; null when unknown. */
  active: number | null
  limit: number | null
}

interface PriceAlertsViewState {
  filters: Record<string, PriceAlertStatusFilter>
  counts: Record<string, PriceAlertCounts | null>
  /** The element the open "new alert" popover hangs from, per panel. Absent or null: closed. */
  adding: Record<string, HTMLElement | null>
  setFilter: (panelId: string, filter: PriceAlertStatusFilter) => void
  setCounts: (panelId: string, counts: PriceAlertCounts | null) => void
  /** Open the popover anchored to `el`; if it is already open, close it. */
  toggleAdd: (panelId: string, el: HTMLElement) => void
  closeAdd: (panelId: string) => void
  /** Move an open popover to another anchor (its draft stays); a closed one stays closed. */
  reanchorAdd: (panelId: string, el: HTMLElement) => void
  /** Drop everything held for a panel; called when the panel is closed. */
  clearPanel: (panelId: string) => void
}

export const DEFAULT_PRICE_ALERT_FILTER: PriceAlertStatusFilter = 'active'

export const usePriceAlertsViewStore = create<PriceAlertsViewState>((set) => ({
  filters: {},
  counts: {},
  adding: {},
  setFilter: (panelId, filter) =>
    set((s) =>
      s.filters[panelId] === filter ? s : { filters: { ...s.filters, [panelId]: filter } },
    ),
  setCounts: (panelId, next) =>
    set((s) => {
      const prev = s.counts[panelId]
      if (prev === next) return s
      if (
        prev &&
        next &&
        prev.total === next.total &&
        prev.waiting === next.waiting &&
        prev.active === next.active &&
        prev.limit === next.limit
      )
        return s
      return { counts: { ...s.counts, [panelId]: next } }
    }),
  toggleAdd: (panelId, el) =>
    set((s) => ({ adding: { ...s.adding, [panelId]: s.adding[panelId] ? null : el } })),
  closeAdd: (panelId) =>
    set((s) => (s.adding[panelId] ? { adding: { ...s.adding, [panelId]: null } } : s)),
  reanchorAdd: (panelId, el) =>
    set((s) => (s.adding[panelId] ? { adding: { ...s.adding, [panelId]: el } } : s)),
  clearPanel: (panelId) =>
    set((s) => {
      if (!(panelId in s.filters) && !(panelId in s.counts) && !(panelId in s.adding)) return s
      const { [panelId]: _f, ...filters } = s.filters
      const { [panelId]: _c, ...counts } = s.counts
      const { [panelId]: _a, ...adding } = s.adding
      return { filters, counts, adding }
    }),
}))

/** The panel's status filter (Active until the user picks another). */
export function usePriceAlertFilter(panelId: string): PriceAlertStatusFilter {
  return usePriceAlertsViewStore((s) => s.filters[panelId] ?? DEFAULT_PRICE_ALERT_FILTER)
}
